import styled from "styled-components";
import { DeleteIcon } from "../../assets/icons/deleteIcon";
import { UploadRule, UPLOAD_MESSAGES } from "../../constants/uploadRules";
import { designSystemVars as vars } from "../tokens";
import { ButtonBase } from "./Button";
import { FileUploadRoot } from "./FileUpload";

interface SingleFileUploadFieldProps {
  file: File | null;
  onFileChange: (file: File | null) => void;
  rule: UploadRule;
  ariaLabel?: string;
  disabled?: boolean;
}

const FileField = styled.div`
  position: relative;
  box-sizing: border-box;
`;

const FilePicker = styled(FileUploadRoot)`
  box-sizing: border-box;
  display: flex;
  width: 100%;
  flex-direction: row;
  align-items: center;
  gap: 0;
  padding: ${vars.spacing.md} var(--inzeloop-ds-spacing-64px) ${vars.spacing.md}
    ${vars.spacing.xl};
  border: 0;
  border-radius: ${vars.radius.sm};
  background: ${vars.color.control.backgroundSubtle};
  color: ${vars.color.control.text};
  font: inherit;
  text-align: left;
  cursor: default;

  &[aria-disabled="true"] {
    color: ${vars.color.text.disabled};
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: 2px solid ${vars.color.border.strong};
    outline-offset: 2px;
  }
`;

const RemoveFileButton = styled(ButtonBase).attrs({ type: "button" })`
  position: absolute;
  top: 10px;
  right: 8px;
  box-sizing: border-box;
  display: flex;
  padding: 0;
  border: 0;
  background: ${vars.color.background.transparent};

  &:focus-visible {
    outline: 2px solid ${vars.color.border.strong};
    outline-offset: 2px;
  }
`;

export const SingleFileUploadField = ({
  file,
  onFileChange,
  rule,
  ariaLabel = "Choose file",
  disabled = false,
}: SingleFileUploadFieldProps) => (
  <FileField>
    <FilePicker
      aria-label={ariaLabel}
      inputAriaLabel={`${ariaLabel} input`}
      accept={rule.accept}
      disabled={disabled}
      onFilesSelected={(files) => onFileChange(files[0] ?? null)}
    >
      {file?.name || UPLOAD_MESSAGES.emptySelection}
    </FilePicker>

    {file && (
      <RemoveFileButton
        aria-label={`Remove ${file.name}`}
        disabled={disabled}
        onClick={() => onFileChange(null)}
      >
        <DeleteIcon />
      </RemoveFileButton>
    )}
  </FileField>
);
